"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type GolfDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * Late-afternoon approach shot: warm sun low on the right, hazy hills and a
 * treeline, a striped fairway rolling up to a raised green with the flag
 * snapping in the breeze (the focal point), a bunker guarding its front-left
 * and a pond glinting behind it on the right. A big oak frames the left; a
 * ball sits teed up in the right foreground. The fairway runs under the
 * participant pill.
 *
 * Motion: the flag ripples and two clouds drift — CSS transform only, off
 * under reduced motion.
 */
const GOLF_KEYFRAMES = `
@keyframes golf-flag {
  0%, 100% { transform: skewY(0deg) scaleX(1) }
  50% { transform: skewY(-5deg) scaleX(0.92) }
}
@keyframes golf-cloud {
  0%, 100% { transform: translate3d(-6px, 0, 0) }
  50% { transform: translate3d(6px, 0, 0) }
}
.golf-flag { animation: golf-flag 1.8s ease-in-out infinite; transform-box: fill-box; transform-origin: left center }
.golf-cloud { animation: golf-cloud 18s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .golf-flag, .golf-cloud { animation: none }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `golf-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("fairway")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#7cc350" />
          <stop offset="0.5" stopColor="#4f9d36" />
          <stop offset="1" stopColor="#2c6b24" />
        </linearGradient>
        <radialGradient id={id("green")} cx="0.45" cy="0.35" r="0.7">
          <stop offset="0" stopColor="#b6f07c" />
          <stop offset="0.6" stopColor="#86d55a" />
          <stop offset="1" stopColor="#5fb042" />
        </radialGradient>
        <clipPath id={id("green-clip")}>
          <ellipse cx="150" cy="138" rx="78" ry="19" />
        </clipPath>
        <linearGradient id={id("sand")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff2cf" />
          <stop offset="1" stopColor="#dcc48c" />
        </linearGradient>
        <linearGradient id={id("pond")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#bfe8ff" />
          <stop offset="0.5" stopColor="#4ba3d6" />
          <stop offset="1" stopColor="#246e9e" />
        </linearGradient>
        <radialGradient id={id("ball")} cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.7" stopColor="#eef1f4" />
          <stop offset="1" stopColor="#b9c0c8" />
        </radialGradient>
      </defs>
    </svg>
  );
}

/** Hills, treeline, fairway stripes — stretch with the card. */
function Course({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 98 Q40 82 90 92 Q140 100 180 86 Q230 74 300 90 L300 120 L0 120 Z"
        fill="#8fb9a0"
        opacity="0.75"
      />
      <path
        d="M0 112 Q8 100 16 108 Q24 96 34 106 Q44 98 54 108 Q64 100 76 110 Q90 104 104 112 L196 112 Q206 100 218 108 Q228 96 240 106 Q252 98 262 108 Q274 100 286 108 Q294 102 300 106 L300 122 L0 122 Z"
        fill="#2f6a3a"
      />
      <path
        d="M0 118 Q80 110 150 116 Q220 122 300 114 L300 220 L0 220 Z"
        fill={`url(#${id("fairway")})`}
      />
      {/* Mowing stripes */}
      {[0, 2, 4, 6].map((i) => (
        <polygon
          key={`stripe-${i}`}
          points={`${i * 50 - 20},220 ${i * 50 + 30},220 ${120 + i * 14},118 ${110 + i * 14},118`}
          fill="#ffffff"
          opacity="0.07"
        />
      ))}
    </svg>
  );
}

const OAK_CANOPY = [
  { cx: 14, cy: 82, rx: 26, ry: 20, fill: "#2d6a2e" },
  { cx: 42, cy: 74, rx: 24, ry: 18, fill: "#357a33" },
  { cx: 60, cy: 96, rx: 20, ry: 14, fill: "#2d6a2e" },
  { cx: 22, cy: 106, rx: 22, ry: 14, fill: "#25592a" },
  { cx: 30, cy: 62, rx: 18, ry: 14, fill: "#3f8a3a" },
  { cx: 46, cy: 112, rx: 18, ry: 11, fill: "#25592a" },
];

function Hole({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Clouds */}
      <g className="golf-cloud" fill="#ffffff">
        <g opacity="0.85">
          <ellipse cx="66" cy="44" rx="20" ry="7" />
          <ellipse cx="56" cy="40" rx="10" ry="7" />
          <ellipse cx="72" cy="38" rx="11" ry="8" />
        </g>
        <g opacity="0.7">
          <ellipse cx="226" cy="58" rx="16" ry="5" />
          <ellipse cx="234" cy="54" rx="9" ry="6" />
        </g>
      </g>

      {/* Pond behind the green, with far trees */}
      {[
        [206, 112, 9],
        [222, 108, 11],
        [240, 111, 8],
        [256, 107, 10],
        [274, 110, 8],
      ].map(([cx, cy, r]) => (
        <g key={`far-tree-${cx}`}>
          <ellipse cx={cx} cy={cy} rx={r} ry={r * 1.15} fill="#245a2e" />
          <ellipse
            cx={cx - 2}
            cy={cy - 3}
            rx={r * 0.55}
            ry={r * 0.5}
            fill="#3f7f3e"
          />
        </g>
      ))}
      <ellipse cx="240" cy="126" rx="38" ry="7" fill={`url(#${id("pond")})`} />
      <path
        d="M214 125 H236 M244 128 H262"
        stroke="#ffffff"
        strokeOpacity="0.8"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <path
        d="M276 124 V114 M279 125 V117 M273 125 V118"
        stroke="#3a6a2a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />

      {/* Raised green */}
      <ellipse
        cx="150"
        cy="142"
        rx="84"
        ry="22"
        fill="#000000"
        opacity="0.18"
      />
      <ellipse cx="150" cy="139" rx="82" ry="21" fill="#4c9a34" />
      <ellipse
        cx="150"
        cy="138"
        rx="78"
        ry="19"
        fill={`url(#${id("green")})`}
      />
      <g clipPath={`url(#${id("green-clip")})`}>
        {[-70, -38, -6, 26, 58].map((x) => (
          <polygon
            key={`mow-${x}`}
            points={`${150 + x},158 ${166 + x},158 ${190 + x},118 ${174 + x},118`}
            fill="#ffffff"
            opacity="0.1"
          />
        ))}
      </g>
      <path
        d="M78 132 Q150 112 222 132"
        stroke="#e8ffd0"
        strokeOpacity="0.5"
        strokeWidth="1"
        fill="none"
      />

      {/* Bunker, front-left */}
      <path
        d="M66 154 Q70 144 90 145 Q108 144 112 150 Q110 158 94 158 Q72 160 66 154 Z"
        fill="#3e7d2c"
      />
      <path
        d="M68 153 Q72 146 90 147 Q106 146 109 150 Q107 156 93 156 Q74 158 68 153 Z"
        fill={`url(#${id("sand")})`}
      />
      <path
        d="M72 150 Q86 146 104 148"
        stroke="#c7ad72"
        strokeOpacity="0.6"
        strokeWidth="0.8"
        fill="none"
      />

      {/* Pin, flag, hole, ball */}
      <path
        d="M162 139 L198 147"
        stroke="#1f4a18"
        strokeOpacity="0.25"
        strokeWidth="1.6"
      />
      <ellipse cx="162" cy="139" rx="4.2" ry="1.5" fill="#173a12" />
      <rect x="161" y="70" width="2" height="69" rx="1" fill="#f8f8f4" />
      <rect x="162.2" y="70" width="0.8" height="69" fill="#b8b8b0" />
      <path
        className="golf-flag"
        d="M163 71 Q172 70 186 75 Q178 79 186 85 Q172 82 163 86 Z"
        fill="#e8333f"
      />
      <path
        d="M163 71 Q172 70 186 75"
        stroke="#ff9aa0"
        strokeWidth="0.8"
        fill="none"
      />
      <circle cx="140" cy="141" r="2.6" fill={`url(#${id("ball")})`} />
      <ellipse
        cx="141"
        cy="143.4"
        rx="2.8"
        ry="0.8"
        fill="#1f4a18"
        opacity="0.3"
      />

      {/* Oak, left flank */}
      <path
        d="M28 196 Q30 160 24 136 Q20 120 30 100 M26 140 Q40 124 56 118"
        stroke="#4a3220"
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
      />
      <ellipse cx="30" cy="200" rx="26" ry="4" fill="#173d16" opacity="0.5" />
      {OAK_CANOPY.map((c) => (
        <g key={`oak-${c.cx}-${c.cy}`}>
          <ellipse cx={c.cx} cy={c.cy} rx={c.rx} ry={c.ry} fill={c.fill} />
          <path
            d={`M${c.cx - c.rx * 0.6} ${c.cy - c.ry * 0.5} Q${c.cx + 2} ${c.cy - c.ry * 1.05} ${c.cx + c.rx * 0.7} ${c.cy - c.ry * 0.3}`}
            stroke="#c9f08a"
            strokeOpacity="0.45"
            strokeWidth="1.2"
            fill="none"
          />
        </g>
      ))}

      {/* Ball teed up, right foreground */}
      <ellipse cx="266" cy="196" rx="13" ry="3" fill="#173d16" opacity="0.45" />
      <path d="M262 184 L270 184 L267 196 L265 196 Z" fill="#e9c27a" />
      <circle cx="266" cy="175" r="9.5" fill={`url(#${id("ball")})`} />
      {[
        [262, 171],
        [267, 170],
        [271, 174],
        [263, 176],
        [268, 178],
        [272, 179],
      ].map(([x, y]) => (
        <circle
          key={`dimple-${x}-${y}`}
          cx={x}
          cy={y}
          r="0.9"
          fill="#9aa3ad"
          opacity="0.55"
        />
      ))}
    </svg>
  );
}

export function GolfDisplayPreview({ participants }: GolfDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Golf"
      backgroundClassName="bg-sky-500"
      participants={participants}
      scene={
        <>
          <style href="golf-scene-keyframes" precedence="default">
            {GOLF_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Sky with a low warm sun on the right */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 84% 22%, #fffbe8 0 9px, rgba(255,240,200,0.75) 11px, rgba(255,226,170,0.3) 30px, transparent 70px), radial-gradient(60% 55% at 84% 30%, rgba(255,214,150,0.45) 0%, transparent 70%), linear-gradient(180deg, #4c9ee0 0%, #8cc8f0 32%, #d6ecf5 48%, #ffe8c4 56%)",
            }}
          />
          <Course id={id} />
          <Hole id={id} />
        </>
      }
      overlays={
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background:
              "radial-gradient(ellipse 120% 120% at 50% 40%, transparent 58%, rgba(10,40,20,0.4) 100%)",
          }}
        />
      }
    />
  );
}
